// shared-common.module.ts - Foundation module for shared utilities
// This module can be safely imported by feature modules without creating circular dependencies

// ---------------------------------------------------------------------
// 1. ANGULAR CORE IMPORTS
// ---------------------------------------------------------------------
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

// ---------------------------------------------------------------------
// 2. THIRD PARTY LIBRARIES
// ---------------------------------------------------------------------
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgxTippyModule } from 'ngx-tippy-wrapper';
import { MarkdownModule } from 'ngx-markdown';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { TagInputModule } from 'ngx-chips';
import { FlatpickrModule } from 'angularx-flatpickr';
import {
  LucideAngularModule,
  icons,
  Fingerprint,
  CheckCircle,
  XCircle,
  Check,
  Clock,
  Activity,
  VolumeX,
  Volume2,
  Edit,
  HelpCircle,
  MessageCircle,
  Send,
  X,
  Trash2,
  Trash,
  UserRound,
  Filter,
  BookOpen,
  ShieldCheck,
  UserPlus,
  ChevronLeft,
  User,
  Briefcase,
  Mail,
  Timer,
  CheckCircle2,
  ListChecks,
  Save,
  Info,
  AlertCircle,
  Cpu,
  Loader2,
} from 'lucide-angular';

// ---------------------------------------------------------------------
// 3. DIRECTIVES
// ---------------------------------------------------------------------
import { CardLoaderDirectives } from './../directives/card-loader.directive';
import { HorizontalScrollDirective } from './../directives/horizontal-scroll.directive';
import { InputAutoFocusDirective } from './../directives/input-auto-focus.directive';
import { LearmoStickDirective } from './../directives/learmo-stick.directive';
import { LearmoDropdownDirectives } from './../directives/ui/learmo-dropdown.directive';
import { LearmoVisibleDirective } from './../directives/ui/learmo-element-visibility.directive';
import { NumericDirective } from '../directives/numeric.directive';
import { PercentageDirective } from '../directives/percentage.directive';
import { ScrollSnapDirective } from './../directives/scroll-snap.directive';
import { StepAnimationDirective } from '../directives/step-animation.directive';
import { StickybitsDirective } from './../directives/stickybit.directive';
import { TextareaAutoresizeDirective } from './../directives/textarea-autoresize.directive';
import {
  TransitionGroupComponent,
  TransitionGroupItemDirective,
} from '../directives/transition-group-item.directive';

// ---------------------------------------------------------------------
// 4. PIPES
// ---------------------------------------------------------------------
import { CallbackFilterPipe } from './pipes/callback-filter.pipe';
import { TimePipe } from './pipes/dateago.pipe';
import { DurationPipe } from './pipes/duration.pipe';
import { FileSizePipe } from './pipes/file-size.pipe';
import { FindPipe } from './pipes/find.pipe';
import { FullNamePipe } from './pipes/fullname.pipe';
import { GeniricFilterPipe } from './pipes/generic-filter.pipe';
import { GeniricSortPipe } from './pipes/generic-sort.pipe';
import { MediaLibraryFilterPipe } from './pipes/media-library-filter.pipe';
import { NumberSuffixPipe } from './pipes/numberSuffix.pipe';
import { RemoveSpacesPipe } from './pipes/remove-spaces.pipe';
import { ReplacePipe } from './pipes/replace.pipe';
import { SafePipe } from './pipes/safe.pipe';
import { SortSelectedFirstPipe } from './pipes/sort-selected-first.pipe';
import { SpecialFilterPipe } from './pipes/special-filter.pipe';
import { UnslugifyPipe } from './pipes/unslugify.pipe';

// ---------------------------------------------------------------------
// 5. SHARED GENERIC COMPONENTS
// ---------------------------------------------------------------------
import { LearmoLogoComponent } from './partials/learmo-logo/learmo-logo.component';
import { ButtonViewComponent } from './partials/dynamic-preview-buttons/button-view/button-view.component';
import { DynamicActionButtonsComponent } from './partials/dynamic-preview-buttons/dynamic-action-buttons.component';
import { SystemLabelComponent } from './partials/publish-status-badges/system-label/system-label.component';
import { DraftLabelComponent } from './partials/publish-status-badges/draft-label/draft-label.component';
import { PublishedLabelComponent } from './partials/publish-status-badges/published-label/published-label.component';
import { IconsSelectorComponent } from './partials/icons-selector/icons-selector.component';
import { LinkViewComponent } from './partials/link-view/link-view.component';
import { StatsItemComponent } from './partials/stats-item/stats-item.component';
import { EmailVewWithCopyComponent } from './partials/email-vew-with-copy/email-vew-with-copy.component';
import { InfoTipComponent } from './partials/info-tip/info-tip.component';
import { DynamicInfoTooltipComponent } from './partials/dynamic-info-tooltip/dynamic-info-tooltip.component';
import { DynamicDropdownComponent } from './partials/dynamic-dropdown/dynamic-dropdown.component';
import { InitialAvatarComponent } from './partials/avatar-initials/avatar-initials.component';
import { GlobalCountryFlagComponent } from './partials/global-country-flag/global-country-flag.component';
import { GlobalRadioInfoCardComponent } from './partials/global-fields/global-radio-info-card/global-radio-info-card.component';
import { NumberTrackerComponent } from './partials/number-tracker/number-tracker.component';
import { AnalyticsIconComponent } from './partials/analytics-icon/analytics-icon.component';

import { DynamicItemsCardComponent } from './partials/dynamic-items-card/dynamic-items-card.component';
import { DynamicCarousellComponent } from './partials/dynamic-carousell/dynamic-carousell.component';
import { DynamicCheckboxCardComponent } from './partials/dynamic-checkbox-card/dynamic-checkbox-card.component';
import { DynamicFormComponent } from './partials/dynamic-form/dynamic-form.component';
import { HeadFilterSearchComponent } from './partials/head-filter-search/head-filter-search.component';
import { PaymentMethodViewComponent } from './partials/payment-method-view/payment-method-view.component';
import { ListThumbnailComponent } from './partials/main-lists/list-thumbnail/list-thumbnail.component';
import { ProductOnboardingGalleryComponent } from './partials/global-onboarding/galleries/product-onboarding/product-onboarding.component';
import { CreatePagePopupComponent } from '../components/user-dashboard/portal/view/portal-content/portal-pages/create-page-popup/create-page-popup.component';
import { GoToPortalComponent } from './partials/go-to-portal/go-to-portal.component';
import { MoneyModelPreviewComponent } from './partials/money-model-preview/money-model-preview.component';
// import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { FunnelOnboardingGalleryComponent } from './partials/global-onboarding/galleries/funnel-onboarding/funnel-onboarding.component';
import { SellingOnboardingGalleryComponent } from './partials/global-onboarding/galleries/selling-onboarding/selling-onboarding.component';
import { DomainPortalSettingsComponent } from '../components/user-dashboard/domains/domain-item/domain-portal-settings/domain-portal-settings.component';
import { DomainDetailsPopoverComponent } from '../components/user-dashboard/domains/domain-item/domain-details-popover/domain-details-popover.component';
import { DomainDetailsCardComponent } from '../components/user-dashboard/domains/domain-item/domain-details-card/domain-details-card.component';

import { LoginMethodItemComponent } from '../components/auth/login-alt/user-login-form-alt/login-method-item/login-method-item.component';
import { UserLoginFormAltComponent } from '../components/auth/login-alt/user-login-form-alt/user-login-form-alt.component';
import { ProfilePicViewComponent } from '../components/profile/profile-main-page/profile-pic-input/profile-pic-view/profile-pic-view.component';
import { RegisterComponent } from '../components/register/register.component';
import { InvitationNeededComponent } from '../components/invitation-needed/invitation-needed.component';
import { PasswordMeterComponent } from '../components/register/user-register-form/password-meter/password-meter.component';
import { UserRegisterFormComponent } from '../components/register/user-register-form/user-register-form.component';
import { ContactTypeViewComponent } from '../components/user-dashboard/contacts/create/contact-type-view/contact-type-view.component';
import { AccountStepsTrackingSidewidgetComponent } from '../components/user-dashboard/dashboard/setup-steps-widget/account-steps-tracking-sidewidget/account-steps-tracking-sidewidget.component';
import { AccountSetupItemComponent } from '../components/user-dashboard/dashboard/setup-steps-widget/setup-steps/account-setup-item/account-setup-item.component';
import { SetupStepsComponent } from '../components/user-dashboard/dashboard/setup-steps-widget/setup-steps/setup-steps.component';
import { FunnelCreateComponent } from '../components/user-dashboard/funnel/create/funnel-create/funnel-create.component';
import { BuilderHeaderComponent } from '../components/user-dashboard/funnel/edit/edit-funnel/funnel-email-edit/html-builder/builder-header/builder-header.component';
import { HtmlBuilderComponent } from '../components/user-dashboard/funnel/edit/edit-funnel/funnel-email-edit/html-builder/html-builder.component';
import { FunnelPublishedDraftLabelComponent } from '../components/user-dashboard/funnel/edit/edit-funnel/funnel-published-draft-label/funnel-published-draft-label.component';
import { GetFunnelLinkComponent } from '../components/user-dashboard/funnel/edit/edit-funnel/get-funnel-link/get-funnel-link.component';
import { FunnelEditDetailsComponent } from '../components/user-dashboard/funnel/edit/funnel-edit-details/funnel-edit-details.component';

import { SalesItemPopupComponent } from '../components/user-dashboard/owner-earnings/sales/sales-item/sales-item-popup/sales-item-popup.component';
import { SalesItemViewComponent } from '../components/user-dashboard/owner-earnings/sales/sales-item/sales-item-view/sales-item-view.component';
import { ProductCreateWizardComponent } from '../components/user-dashboard/product/create/product-create-wizard/product-create-wizard.component';
import { ProductTypeMoreDetailsComponent } from '../components/user-dashboard/product/create/product-create-wizard/product-type-wizard-step/product-type-more-details/product-type-more-details.component';
import { ProductTypePreviewComponent } from '../components/user-dashboard/product/create/product-create-wizard/product-type-wizard-step/product-type-preview/product-type-preview.component';
import { ProductCreateComponent } from '../components/user-dashboard/product/create/product-create/product-create.component';
import { GetProductLinkComponent } from '../components/user-dashboard/product/product-item/get-product-link/get-product-link.component';
import { RefundItemPopupComponent } from '../components/user-dashboard/refund/refund-item/refund-item-popup/refund-item-popup.component';
import { WaitListFormComponent } from '../components/wait-list/wait-list-form/wait-list-form.component';
import { WaitListComponent } from '../components/wait-list/wait-list.component';
import { SwiperWrapperComponent } from './partials/swiper-wrapper/swiper-wrapper.component';
import {
  SwiperContainerDirective,
  SwiperSlideDirective,
} from './partials/swiper-wrapper/swiper-custom-elements.directive';
import { ApplicationLoadingComponentComponent } from './layout/owner-dashboard/application-loading-component/application-loading-component.component';
import { LearmoTargetComponent } from './layout/owner-dashboard/learmo-target/learmo-target.component';
import { MainNavBarListComponent } from './layout/owner-dashboard/main-nav-bar-list/main-nav-bar-list.component';
import { MainNavBarComponent } from './layout/owner-dashboard/main-nav-bar/main-nav-bar.component';
import { OwnerDynamicBackButtonComponent } from './layout/owner-dashboard/owner-dynamic-back-button/owner-dynamic-back-button.component';
import { PointerGuideComponent } from './layout/owner-dashboard/pointer-guide/pointer-guide.component';
import { WebsiteFinalizingComponent } from './layout/owner-dashboard/website-finalizing/website-finalizing.component';
import { VerificationReminderComponent } from './layout/owner-dashboard/verification-reminder/verification-reminder.component';
import { FastActionsButtonsComponent } from './layout/top-menu/fast-actions-buttons/fast-actions-buttons.component';
import { OwnerNotificationTypeLabelComponent } from './layout/top-menu/fast-actions-buttons/owner-notifications-list/owner-notification-element/owner-noti-type-label/owner-noti-type-label.component';
import { OwnerNotificationElementComponent } from './layout/top-menu/fast-actions-buttons/owner-notifications-list/owner-notification-element/owner-notification-element.component';
import { OwnerNotificationsListComponent } from './layout/top-menu/fast-actions-buttons/owner-notifications-list/owner-notifications-list.component';
import { TopMenuComponent } from './layout/top-menu/top-menu.component';
import { BrowserAddressBarComponent } from './partials/browser-address-bar/browser-address-bar.component';
import { BrowserWebsiteViewComponent } from './partials/browser-website-view/browser-website-view.component';
import { CategorySelectorComponent } from './partials/category-selector/category-selector.component';
import { DashboardStatsCardComponent } from './partials/dashboard-stats-card/dashboard-stats-card.component';
import { DynamicSecondLevelNavComponent } from './partials/dynamic-second-level-nav/dynamic-second-level-nav.component';
import { DynamicTopLevelNavComponent } from './partials/dynamic-top-level-nav/dynamic-top-level-nav.component';
import { FullScreenDynamicHeaderComponent } from './partials/full-screen-dynamic-header/full-screen-dynamic-header.component';
import { MoneyInputComponent } from './partials/global-fields/money-input/money-input.component';
import { OfferSelectorComponent } from './partials/global-fields/offer-selector/offer-selector.component';
import { ProductSelectorComponent } from './partials/global-fields/product-selector/product-selector.component';
import { GlobalLearmoModalComponent } from './partials/global-learmo-modal/global-learmo-modal.component';
import { StudentProfileCardComponent } from './partials/global-popovers/student-profile-card/student-profile-card.component';
import { GlobalSearchHistoryComponent } from './partials/global-search/global-search-history/global-search-history.component';
import { GlobalSearchPreferencesComponent } from './partials/global-search/global-search-preferences/global-search-preferences.component';
import { GlobalSearchResultsBoxComponent } from './partials/global-search/global-search-results-box/global-search-results-box.component';
import { GlobalSearchComponent } from './partials/global-search/global-search.component';
import { HeadTitleAddComponent } from './partials/head-title-add/head-title-add.component';
import { InstructorSelectorComponent } from './partials/instructor-selector/instructor-selector.component';
import { LanguageSelectorComponent } from './partials/language-selector/language-selector.component';
import { EditorToolbarComponent } from './partials/learmo-block-editor/components/editor-toolbar/editor-toolbar.component';
import { LearmoBlockEditorComponent } from './partials/learmo-block-editor/components/learmo-block-editor.component';
import { LearmoEditorComponent } from './partials/learmo-editor/learmo-editor.component';
import { PeriodButtonComponent } from './partials/period-button/period-button.component';
import { PeriodDateNavigatorComponent } from './partials/period-date-navigator/period-date-navigator.component';
import { PricingInputComponent } from './partials/pricing-input/pricing-input.component';
import { PricingSliderComponent } from './partials/pricing-slider/pricing-slider.component';
import { ItemHeadSkeletonComponent } from './partials/skeletons/item-head-skeleton/item-head-skeleton.component';
import { SystemItemsSequenceComponent } from './partials/system-items-sequence/system-items-sequence.component';
import { AffiliateDropComponent } from './partials/user-drop/affiliate-drop/affiliate-drop.component';
import { GetHelpMenuComponent } from './partials/user-drop/get-help-menu/get-help-menu.component';
import { OwnerDropComponent } from './partials/user-drop/owner-drop/owner-drop.component';
import { UserDropComponent } from './partials/user-drop/user-drop.component';
import { UsersSymbolsGroupComponent } from './partials/users-symbols-group/users-symbols-group.component';
import { NotificationToastComponent } from './services/notification-toast/notification-toast.component';

import { AiOrbComponent } from './partials/ai/ai-thinking-loader/ai-orb/ai-orb.component';
import { AiThinkingLoaderComponent } from './partials/ai/ai-thinking-loader/ai-thinking-loader.component';
import { DragulaModule } from 'ng2-dragula';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { NgxSkeletonModule } from 'ngx-skeleton';
import { NgOtpInputModule } from 'ng-otp-input';
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input-gg';

import { SystemPaymentMethodViewComponent } from './partials/system-payment-method-view/system-payment-method-view.component';

import { ColorPaletteSelectorComponent } from './layout/owner-dashboard/account-setup-wizard/steps/hub-branding-step/color-palette-selector/color-palette-selector.component';

// ---------------------------------------------------------------------
// 6. CONSTANT GROUPINGS
// ---------------------------------------------------------------------

const DIRECTIVES = [
  CardLoaderDirectives,
  HorizontalScrollDirective,
  InputAutoFocusDirective,
  LearmoDropdownDirectives,
  LearmoStickDirective,
  LearmoVisibleDirective,
  NumericDirective,
  PercentageDirective,
  ScrollSnapDirective,
  StepAnimationDirective,
  StickybitsDirective,
  TextareaAutoresizeDirective,
  TransitionGroupComponent,
  TransitionGroupItemDirective,
];

const PIPES = [
  CallbackFilterPipe,
  FileSizePipe,
  FindPipe,
  FullNamePipe,
  GeniricFilterPipe,
  GeniricSortPipe,
  MediaLibraryFilterPipe,
  NumberSuffixPipe,
  RemoveSpacesPipe,
  ReplacePipe,
  SafePipe,
  SortSelectedFirstPipe,
  SpecialFilterPipe,
  TimePipe,
  DurationPipe,
  UnslugifyPipe,
];

const SHARED_COMPONENTS = [
  LearmoLogoComponent,
  ButtonViewComponent,
  DynamicActionButtonsComponent,
  SystemLabelComponent,
  DraftLabelComponent,
  PublishedLabelComponent,
  IconsSelectorComponent,
  LinkViewComponent,
  StatsItemComponent,
  EmailVewWithCopyComponent,
  InfoTipComponent,
  DynamicInfoTooltipComponent,
  DynamicDropdownComponent,
  InitialAvatarComponent,
  GlobalCountryFlagComponent,
  GlobalRadioInfoCardComponent,
  NumberTrackerComponent,
  AnalyticsIconComponent,
  DynamicItemsCardComponent,
  DynamicCarousellComponent,
  DynamicCheckboxCardComponent,
  DynamicFormComponent,
  HeadFilterSearchComponent,
  PaymentMethodViewComponent,
  ListThumbnailComponent,
  GoToPortalComponent,
  MoneyModelPreviewComponent,

  SwiperWrapperComponent,

  ProductOnboardingGalleryComponent,
  SellingOnboardingGalleryComponent,
  FunnelOnboardingGalleryComponent,

  CreatePagePopupComponent,

  DomainDetailsCardComponent,
  DomainDetailsPopoverComponent,
  DomainPortalSettingsComponent,

  SystemPaymentMethodViewComponent,
  SwiperContainerDirective,
  SwiperSlideDirective,
];

const REMAINING_SHARED_COMPONENTS = [
  VerificationReminderComponent,
  ColorPaletteSelectorComponent,

  AiThinkingLoaderComponent,
  AiOrbComponent,

  // Layout & Navigation
  TopMenuComponent,

  MainNavBarComponent,
  MainNavBarListComponent,
  DynamicTopLevelNavComponent,
  DynamicSecondLevelNavComponent,
  FullScreenDynamicHeaderComponent,
  OwnerDynamicBackButtonComponent,
  ApplicationLoadingComponentComponent,
  FastActionsButtonsComponent,
  OwnerNotificationElementComponent,
  OwnerNotificationTypeLabelComponent,
  OwnerNotificationsListComponent,
  NotificationToastComponent,

  // Auth & Onboarding
  RegisterComponent,
  InvitationNeededComponent,
  UserRegisterFormComponent,
  UserLoginFormAltComponent,
  LoginMethodItemComponent,
  PasswordMeterComponent,
  SetupStepsComponent,
  AccountSetupItemComponent,
  AccountStepsTrackingSidewidgetComponent,
  WebsiteFinalizingComponent,

  // Editors
  LearmoEditorComponent,
  LearmoBlockEditorComponent,
  EditorToolbarComponent,

  // Products, Offers, Domains
  ProductCreateComponent,
  ProductCreateWizardComponent,
  ProductTypePreviewComponent,
  ProductTypeMoreDetailsComponent,
  GetProductLinkComponent,
  ContactTypeViewComponent,
  AffiliateDropComponent,
  ProfilePicViewComponent,

  // Funnels
  FunnelCreateComponent,
  FunnelEditDetailsComponent,
  FunnelPublishedDraftLabelComponent,
  GetFunnelLinkComponent,
  HtmlBuilderComponent,
  BuilderHeaderComponent,

  // Refunds & Sales
  RefundItemPopupComponent,
  SalesItemPopupComponent,
  SalesItemViewComponent,

  // Global UI Components
  WaitListComponent,
  WaitListFormComponent,
  BrowserWebsiteViewComponent,
  BrowserAddressBarComponent,
  LanguageSelectorComponent,
  InstructorSelectorComponent,
  UsersSymbolsGroupComponent,
  OfferSelectorComponent,
  SystemItemsSequenceComponent,
  MoneyInputComponent,
  ProductSelectorComponent,
  LearmoTargetComponent,
  PointerGuideComponent,
  StudentProfileCardComponent,
  PricingInputComponent,
  PricingSliderComponent,
  ItemHeadSkeletonComponent,

  HeadTitleAddComponent,
  UserDropComponent,
  OwnerDropComponent,
  PeriodButtonComponent,
  PeriodDateNavigatorComponent,
  DashboardStatsCardComponent,
  GlobalLearmoModalComponent,
  GlobalSearchHistoryComponent,
  GlobalSearchPreferencesComponent,
  GlobalSearchResultsBoxComponent,
  GlobalSearchComponent,
  GetHelpMenuComponent,
  CategorySelectorComponent,
];
const ALL_DECLARATIONS = [
  ...DIRECTIVES,
  ...PIPES,
  ...SHARED_COMPONENTS,
  ...REMAINING_SHARED_COMPONENTS,
];

// ---------------------------------------------------------------------
// 6. MODULE DEFINITION
// ---------------------------------------------------------------------
/**
 * SharedCommonModule
 *
 * Purpose: Provides foundational shared utilities that can be safely imported
 * by feature modules without creating circular dependencies.
 *
 * Contains:
 * - Directives for UI behavior
 * - Pipes for data transformation
 * - Re-exports of commonly used Angular and third-party modules
 *
 * This module does NOT contain any components that depend on feature modules.
 */
@NgModule({
  declarations: ALL_DECLARATIONS,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    NgbModule,
    NgSelectModule,
    NgxTippyModule,
    MarkdownModule,
    AngularSvgIconModule,
    TagInputModule,
    NgxSkeletonLoaderModule.forRoot(),
    NgxSkeletonModule,
    NgxIntlTelInputModule,
    DragulaModule.forRoot(),
    FlatpickrModule.forRoot(),
    LucideAngularModule.pick({
      ...icons,
      Fingerprint,
      CheckCircle,
      XCircle,
      Check,
      Clock,
      Activity,
      VolumeX,
      Volume2,
      Edit,
      HelpCircle,
      MessageCircle,
      Send,
      X,
      Trash2,
      Trash,
      UserRound,
      Filter,
      BookOpen,
      ShieldCheck,
      UserPlus,
      ChevronLeft,
      User,
      Briefcase,
      Mail,
      Timer,
      CheckCircle2,
      ListChecks,
      Save,
      Info,
      AlertCircle,
      Cpu,
      Loader2,
    }),
    NgOtpInputModule,
  ],
  exports: [
    // Angular modules
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,

    // Third-party modules
    NgbModule,
    NgSelectModule,
    NgxTippyModule,
    MarkdownModule,
    // NgxSkeletonLoaderModule,
    AngularSvgIconModule,
    TagInputModule,
    DragulaModule,
    FlatpickrModule,
    LucideAngularModule,
    NgxSkeletonLoaderModule,
    NgxSkeletonModule,
    NgxIntlTelInputModule,
    NgOtpInputModule,
    // Directives and pipes
    ...ALL_DECLARATIONS,
  ],
  schemas: [],
})
export class SharedCommonModule {}
